import React from 'react';
import { Bell, Check, CheckCheck, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useNotifications, NotificationCategory, NotificationType } from '@/hooks/useNotifications';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';

export const NotificationCenter = () => {
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead } = useNotifications();

  const getNotificationIcon = (type: NotificationType) => {
    const iconClass = "h-4 w-4";
    switch (type) {
      case 'success':
        return <Check className={`${iconClass} text-green-500`} />;
      case 'warning':
        return <Bell className={`${iconClass} text-yellow-500`} />;
      case 'error':
        return <X className={`${iconClass} text-red-500`} />;
      default:
        return <Bell className={`${iconClass} text-blue-500`} />;
    }
  };

  const getCategoryBadge = (category: NotificationCategory) => {
    const variants: Record<NotificationCategory, any> = {
      booking: 'default',
      payment: 'secondary',
      system: 'outline',
      hotel: 'default',
      review: 'secondary',
      security: 'destructive'
    };

    const labels: Record<NotificationCategory, string> = {
      booking: 'Бронирование',
      payment: 'Платеж',
      system: 'Система',
      hotel: 'Отель',
      review: 'Отзыв',
      security: 'Безопасность'
    };

    return (
      <Badge variant={variants[category]} className="text-xs">
        {labels[category]}
      </Badge>
    );
  };

  const getNotificationBg = (type: NotificationType, isRead: boolean, priority: number) => {
    const baseClasses = isRead ? 'bg-muted/30' : 'bg-background';
    const priorityClasses = priority >= 3 ? 'ring-1 ring-red-200' : priority >= 2 ? 'ring-1 ring-yellow-200' : '';
    
    switch (type) {
      case 'success':
        return `${baseClasses} border-l-4 border-l-green-500 ${priorityClasses}`;
      case 'warning':
        return `${baseClasses} border-l-4 border-l-yellow-500 ${priorityClasses}`;
      case 'error':
        return `${baseClasses} border-l-4 border-l-red-500 ${priorityClasses}`;
      default:
        return `${baseClasses} border-l-4 border-l-blue-500 ${priorityClasses}`;
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold">Уведомления</h3>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={markAllAsRead}
              className="h-auto p-1 text-xs"
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Прочитать все
            </Button>
          )}
        </div>
        
        <ScrollArea className="h-80">
          {loading ? (
            <div className="p-4 text-center text-muted-foreground">
              Загрузка...
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              Нет уведомлений
            </div>
          ) : (
            <div className="space-y-1">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-3 cursor-pointer hover:bg-muted/50 transition-colors ${getNotificationBg(notification.type, notification.is_read, notification.priority || 1)}`}
                  onClick={() => !notification.is_read && markAsRead(notification.id)}
                >
                  <div className="flex items-start gap-3">
                    {getNotificationIcon(notification.type)}
                    <div className="flex-1 space-y-1">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <h4 className={`text-sm font-medium ${notification.is_read ? 'text-muted-foreground' : 'text-foreground'}`}>
                            {notification.title}
                          </h4>
                          {getCategoryBadge(notification.category || 'system')}
                        </div>
                        {!notification.is_read && (
                          <div className="w-2 h-2 bg-primary rounded-full mt-1 ml-2" />
                        )}
                      </div>
                      <p className={`text-xs ${notification.is_read ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
                        {notification.message}
                      </p>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>
                          {formatDistanceToNow(new Date(notification.created_at), { 
                            addSuffix: true, 
                            locale: ru 
                          })}
                        </span>
                        {notification.priority && notification.priority > 1 && (
                          <Badge variant="outline" className="text-xs h-4">
                            Приоритет {notification.priority}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};